// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Wrapper around web fetch for requests to Roof-Calc API.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { baseUrl } from "./base-url.js"
import { alertRequestError } from "./error-modal.js"

// @@imports-types
import { HttpMethod, HTTP_METHODS, requestErrorCb } from "../../types/index.js"

// @@body
/**
 * Execute API request with some default configuration options etc., and error
 * handling and reporting.
 * @param {string} path API path/route to request. Base URL of API is prepended
 * to this path.
 * @param {object} obj Optional destructured parameters object.
 * @param {HttpMethod} [obj.method] HTTP method to use in fetch request.
 * @param {object} [obj.headers] Object containing headers to add to request.
 * Some headers such as "Authorization" and "Content-Type" may be set
 * automatically depending on other parameters.
 * @param {Record<string, string>} [obj.query] Key value object of string query
 * parameters which should be appended to the request URL.
 * @param {object} [obj.body] Object of data to be passed into the request body.
 * This object will be stringified and passed as request body. Setting this
 * parameter will set the "Content-Type" header to be set to "application/json".
 * @param {?string} [obj.token] Authorization JWT token to be used if required.
 * This token will be set the "Authorization" header to "Bearer <token>" if
 * specified.
 * @param {number[]} [obj.allowedCodes] Allowed error response status codes.
 * @param {boolean} [obj.alert] Should alert modal be rendered to App instance?
 * @param {boolean} [obj.reload] Should page be reloaded following error?
 * @param {requestErrorCb} [obj.errorCallback] Optional callback to run *after*
 * error modal is dismissed, and *before* page refresh.
 * @returns {Promise<any>} Promise resolving to JSON data consumed from response
 * body. May result in request error modal being rendered followed by forced
 * page refresh.
 */
const apiRequest = async (path, {
    method = HTTP_METHODS.get,
    headers = {},
    query,
    body,
    token,
    allowedCodes = [],
    alert = true,
    reload = true,
    errorCallback
} = {}) => {
    // Build request URL from path and query parameters.
    let url = `${baseUrl}/${path}`
    if (query) { url += `?${new URLSearchParams(query).toString()}` }

    // Build request options including body data if specified.
    const options = /** @type {any} */ ({ method, headers })
    if (body) {
        options.headers["Content-Type"] = "application/json"
        options.body = JSON.stringify(body)
    }

    // Add authorization header if token specified.
    if (token) { options.headers["Authorization"] = `Bearer ${token}` }

    // Execute request and consume response body as JSON data.
    const response = await fetch(url, options)
    const json = await response.json()

    // Load request error modal if response status code not OK (2xx) and not one
    // of array of custom allowed status codes.
    if (!response.ok && !allowedCodes.includes(response.status)) {
        // Render alert modal if required, and await closure.
        if (alert) { await alertRequestError(json.message, response.status) }

        // Execute error callback if found, and reload page if required,
        // otherwise throw error.
        if (errorCallback) { errorCallback() }
        if (reload) { window.location.reload() }
        else { throw new Error(json.message) }
    }

    return json
}

// @@exports
export { apiRequest }
